// Libraries
import { useState } from "react";
import { Grid } from "@mui/material";

// Utils
import { intersection, not } from "./utils";
import { getSortedOptions } from "../SmartSelector/utils";

// Components
import CustomList from "./components/CustomList";
import SwitchButtonArea from "./components/SwitchButtonArea";

// Icons
import ArrowRight from "@/assets/icons/svg/chevron-right.svg";
import ArrowLeft from "@/assets/icons/svg/chevron-left.svg";

type TSwitchableListsProps<T extends {}> = {
	sections: {
		left: {
			title: string;
			items: T[];
			setItems: (items: T[]) => void;
			icon?: string;
		};
		right: {
			title: string;
			items: T[];
			setItems: (items: T[]) => void;
			icon?: string;
		};
	};
	showBy: keyof T;
	sortBy: keyof T;
};

export default function SwitchableLists<T extends {}>(props: Readonly<TSwitchableListsProps<T>>) {
	const { sections, showBy, sortBy } = props;

	const { left: leftSection, right: rightSection } = sections;

	const { icon: leftIconDirection = ArrowLeft, items: leftItems, setItems: setLeftSide } = leftSection;
	const { icon: rightIconDirection = ArrowRight, items: rightItems, setItems: setRightSide } = rightSection;

	const [checked, setChecked] = useState<T[]>([]);

	const leftChecked = intersection<T>(checked, leftItems);
	const rightChecked = intersection<T>(checked, rightItems);

	const handleMoveToRight = () => {
		setRightSide(rightItems.concat(leftChecked));
		setLeftSide(not(leftItems, leftChecked));
		setChecked(not(checked, leftChecked));
	};

	const handleMoveToLeft = () => {
		setLeftSide(leftItems.concat(rightChecked));
		setRightSide(not(rightItems, rightChecked));
		setChecked(not(checked, rightChecked));
	};

	const sortedLeftList = getSortedOptions<T>(leftItems, sortBy as string) as T[];
	const sortedRightList = getSortedOptions<T>(rightItems, sortBy as string) as T[];

	return (
		<>
			<Grid container sx={{ justifyContent: "center", alignItems: "center" }} className="mb-8">
				<CustomList
					title={leftSection.title}
					items={sortedLeftList}
					checked={checked}
					showItemBy={showBy}
					setChecked={setChecked}
				/>
				<SwitchButtonArea
					moveToLeft={{ icon: rightIconDirection, disabled: leftChecked.length === 0, onClick: handleMoveToRight }}
					moveToRight={{ icon: leftIconDirection, disabled: rightChecked.length === 0, onClick: handleMoveToLeft }}
				/>
				<CustomList
					title={rightSection.title}
					items={sortedRightList}
					checked={checked}
					showItemBy={showBy}
					setChecked={setChecked}
				/>
			</Grid>
		</>
	);
}
